import React, { useEffect, useState } from 'react';
import {
  CARGO_RATIO_LABELS,
  fetchCargoRatioOptions,
  getCargoRatioKey,
} from './utils';
import './CargoRatioSelector.css';
import ActionButton from '../../../../../../Components/Common/ActionButton';
import { Skeleton } from '../../../../../../Components/Common/Skeleton';

const isRatioSelected = (selectedRatios, option) =>
  selectedRatios.some(
    ratio =>
      getCargoRatioKey(ratio.id) === getCargoRatioKey(option.id) ||
      (ratio.label && ratio.label === option.label)
  );

const CargoRatioSelector = ({
  id,
  selectedRatios = [],
  ratioOptions: ratioOptionsProp,
  onToggleRatio,
  onConfirm,
  isSubmitting = false,
  isReadOnly = false,
  hasError = false,
}) => {
  const [fetchedOptions, setFetchedOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const hasProvidedOptions = Array.isArray(ratioOptionsProp);
  const isInteractionLocked = isReadOnly || isSubmitting;

  useEffect(() => {
    if (hasProvidedOptions) return undefined;

    let cancelled = false;

    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const ratioOptions = await fetchCargoRatioOptions();
        if (!cancelled) {
          setFetchedOptions(ratioOptions);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadOptions();

    return () => {
      cancelled = true;
    };
  }, [hasProvidedOptions]);

  const resolvedOptions = hasProvidedOptions
    ? ratioOptionsProp
    : fetchedOptions;

  const displayOptions =
    resolvedOptions.length > 0
      ? resolvedOptions
      : CARGO_RATIO_LABELS.map((label, index) => ({
          id: index + 1,
          label,
        }));

  const hintText = isReadOnly
    ? 'Cargo ratio is locked because HPS Market request has been submitted.'
    : hasError
      ? 'Cargo ratio is required. Please select at least one ratio, then click Select Cargo Ratio.'
      : 'Please select the cargo ratio to proceed.';

  return (
    <div
      id={id}
      tabIndex={-1}
      aria-disabled={isReadOnly || undefined}
      className={`cargo-ratio-selector border rounded-3 p-3 mb-3 ${
        hasError
          ? 'cargo-ratio-selector--error'
          : isReadOnly
            ? 'cargo-ratio-selector--readonly bg-light-subtle'
            : 'bg-light-subtle'
      }`}
    >
      <div className='d-flex align-items-center justify-content-between gap-2 mb-1'>
        <h5 className='cargo-ratio-selector__title fw-semibold mb-0'>
          Cargo Ratio Selection
        </h5>
        {isReadOnly ? (
          <span className='badge bg-secondary-subtle text-secondary fw-medium d-inline-flex align-items-center gap-1'>
            <i className='ri-lock-line'></i>
            Locked
          </span>
        ) : null}
      </div>
      <p
        className={`cargo-ratio-selector__hint small mb-3 ${
          hasError ? '' : 'text-muted'
        }`}
      >
        {hintText}
      </p>

      {isLoadingOptions && !hasProvidedOptions ? (
        <div
          className='d-flex flex-column flex-md-row gap-2'
          role='status'
          aria-busy='true'
        >
          <span className='visually-hidden'>Loading</span>
          {[0, 1, 2].map(index => (
            <Skeleton key={index} width={160} height={38} />
          ))}
        </div>
      ) : (
        <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2'>
          {displayOptions.map(option => {
            const isSelected = isRatioSelected(selectedRatios, option);

            return (
              <button
                key={option.id}
                type='button'
                className={`btn btn-sm px-3 py-2 border rounded-3 d-flex d-md-inline-flex align-items-center gap-2 w-100 w-md-auto transition-all ${
                  isSelected
                    ? 'border-primary bg-primary-subtle text-primary fw-semibold'
                    : isReadOnly
                      ? 'border-secondary-subtle bg-light text-muted'
                      : 'border-secondary-subtle bg-white text-dark shadow-sm'
                }`}
                onClick={() => {
                  if (isInteractionLocked) return;
                  onToggleRatio?.(option);
                }}
                disabled={isInteractionLocked}
                aria-pressed={isSelected}
                title={
                  isReadOnly
                    ? 'Cargo ratio cannot be changed after submission'
                    : undefined
                }
              >
                <span
                  className={`rounded-circle border d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                    isSelected
                      ? 'border-primary bg-primary text-white'
                      : 'border-secondary bg-light'
                  }`}
                  style={{ width: 18, height: 18 }}
                >
                  {isSelected ? (
                    <i className='ri-check-line fs-14 lh-1'></i>
                  ) : null}
                </span>
                <span className='fw-medium text-start'>{option.label}</span>
              </button>
            );
          })}

          {!isReadOnly ? (
            <ActionButton
              variant='primary'
              className='w-100 w-md-auto'
              onClick={onConfirm}
              disabled={isLoadingOptions}
              loading={isSubmitting}
            >
              Select Cargo Ratio
            </ActionButton>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default CargoRatioSelector;
